import { memo, useMemo, useState } from "react";

// HRP v4 returns 1–2 char status codes; claim_fact uses human labels.
// Mapped from live staging samples + HealthRules conventions.
const CLAIM_STATUS_LABELS = {
    f: "Final",
    fi: "Final",
    final: "Final",
    d: "Denied",
    denied: "Denied",
    rj: "Rejected",
    rejected: "Rejected",
    rv: "Needs Review",
    "needs review": "Needs Review",
    rp: "Needs Repair",
    "needs repair": "Needs Repair",
    nr: "Needs Repricing",
    "needs repricing": "Needs Repricing",
    p: "Pended",
    pended: "Pended",
};

const LINE_STATUS_LABELS = {
    a: "Adjudicated",
    d: "Denied",
    r: "Rejected",
    // Repair / pre-adjudication states seen on Needs Repair claims
    i: "Incomplete",
    c: "Converted",
    p: "Pended",
};

export const parseClaimStatus = (code) => {
    if (code == null || code === "") return null;
    const raw = String(code).trim();
    const key = raw.toLowerCase();
    const label = CLAIM_STATUS_LABELS[key];
    if (label) return { code: raw, label, css: label.toLowerCase().replace(/\s+/g, "-") };
    // Already a human label (e.g. from claim_fact)
    if (raw.length > 2) {
        return { code: raw, label: raw, css: key.replace(/\s+/g, "-") };
    }
    return { code: raw, label: raw.toUpperCase(), css: "unknown" };
};

export const parseLineStatus = (code) => {
    if (code == null || code === "") return null;
    const raw = String(code).trim();
    const key = raw.toLowerCase();
    const label = LINE_STATUS_LABELS[key];
    if (label) return { code: raw, label, css: label.toLowerCase().replace(/\s+/g, "-") };
    if (raw.length > 2) {
        return { code: raw, label: raw, css: key.replace(/\s+/g, "-") };
    }
    return { code: raw, label: raw.toUpperCase(), css: "unknown" };
};

const hasMoney = (v) => {
    if (v == null || v === "") return false;
    if (typeof v === "string" && v.trim().startsWith("$")) return true;
    const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
    return Number.isFinite(n);
};

const money = (v) => {
    if (!hasMoney(v)) return null;
    if (typeof v === "string" && v.trim().startsWith("$")) return v;
    const n = Number(String(v).replace(/[^0-9.\-]/g, ""));
    if (!Number.isFinite(n)) return String(v);
    return n.toLocaleString(undefined, {
        style: "currency",
        currency: "USD",
    });
};

/** True when the claim has enough detail to be worth a card (not just an id). */
const hasClaimCardContent = (claim) => {
    if (!claim || typeof claim !== "object") return false;
    if (claim.claimStatus) return true;
    if (claim.memberName || claim.supplierName) return true;
    if (claim.serviceLines?.length > 0) return true;
    if (claim.exceptions?.length > 0 || claim.reviewMessages?.length > 0) return true;
    const t = claim.totals || {};
    if (
        hasMoney(t.chargedAmount)
        || hasMoney(t.allowedAmount)
        || hasMoney(t.paidAmount)
        || hasMoney(t.memberAmount)
    ) {
        return true;
    }
    return false;
};

const pick = (...vals) => {
    for (const v of vals) {
        if (v != null && v !== "") return v;
    }
    return null;
};

const asList = (raw, key) => {
    if (Array.isArray(raw)) return raw;
    if (raw?.[key] != null) {
        return Array.isArray(raw[key]) ? raw[key] : [raw[key]];
    }
    if (raw && typeof raw === "object") return [raw];
    return [];
};

const extractLineDetails = (item) => {
    if (Array.isArray(item?.lines?.lineDetails)) return item.lines.lineDetails;
    if (Array.isArray(item?.lines)) return item.lines;
    return [];
};

const normalizeServiceLine = (line, idx) => {
    if (!line || typeof line !== "object") return null;
    const pricing = line.pricingInfo ?? {};
    const reviews = asList(
        line.reviewMessages ?? line.lineReviewMessages,
        "reviewTrigger",
    ).map((r) => ({
        code: r.code ?? r.reviewMessageCode?.messageCode,
        description: r.description ?? r.reviewMessageCode?.messageDescription,
    }));
    const exceptions = asList(
        line.exceptions ?? line.lineActivityExceptions,
        "exception",
    ).map((e) => ({
        code: e.code ?? e.exceptionMessageCode?.messageCode,
        description: e.description ?? e.exceptionMessageCode?.messageDescription,
        level: e.level ?? e.enforcementLevel,
    }));
    const status = parseLineStatus(line.lineStatus);
    return {
        key: String(line.lineNumber ?? line.hccClaimLineNumber ?? idx + 1),
        lineNumber: pick(line.lineNumber, line.hccClaimLineNumber, idx + 1),
        lineStatus: status?.label ?? line.lineStatus,
        lineStatusCode: status?.code ?? line.lineStatus,
        lineStatusCss: status?.css,
        serviceFromDate: line.serviceFromDate,
        serviceToDate: line.serviceToDate,
        procedureCode: pick(line.procedureCode, line.serviceCode),
        revenueCode: line.revenueCode,
        units: line.units,
        benefitTierName: line.benefitTierName,
        chargedAmount: pick(line.chargedAmount, pricing.chargedAmount),
        allowedAmount: pick(line.allowedAmount, pricing.allowedAmount),
        paidAmount: pick(line.paidAmount, pricing.paidAmount),
        memberResponsibility: pick(
            line.memberResponsibility,
            pricing.memberResponsibilityAmount,
        ),
        coPayAmount: pick(line.coPayAmount, pricing.coPayAmount),
        deductibleAmount: pick(line.deductibleAmount, pricing.deductibleAmount),
        coInsuranceAmount: pick(line.coInsuranceAmount, pricing.coInsuranceAmount),
        reviewMessages: reviews,
        exceptions,
    };
};

/**
 * Detect claim-detail payloads in tool results:
 *  - post-submit: { claimDetails, claimSummary, claimFact, … }
 *  - hrp_get_claim_details: { ok, data: { items: [v4] } }
 *  - batch row: { claimHccId, serviceLines, … }
 */
export const parseClaimToolResult = (content) => {
    if (!content || typeof content !== "string") return null;
    let parsed;
    try {
        parsed = JSON.parse(content);
    } catch {
        return null;
    }
    if (!parsed || typeof parsed !== "object") return null;

    // Batch: prefer rendering each successful row that has real detail.
    if (Array.isArray(parsed.results) && parsed.results.length) {
        const claims = parsed.results
            .filter((r) => r && (r.claimHccId || r.serviceLines?.length))
            .map((r) => ({
                claimHccId: r.claimHccId,
                claimStatus: r.claimStatus,
                claimType: r.claimFactSummary?.claimTypeName,
                externalClaimNumber: r.externalClaimNumber,
                memberName: r.memberName,
                memberId: r.memberId,
                supplierName: r.claimFactSummary?.supplierName,
                totals: {
                    chargedAmount: r.chargedAmount,
                    allowedAmount: r.allowedAmount,
                    paidAmount: r.paidAmount,
                    memberAmount: r.memberAmount,
                },
                serviceLines: (r.serviceLines || [])
                    .map(normalizeServiceLine)
                    .filter(Boolean),
                claimFactSummary: r.claimFactSummary ?? null,
                reviewMessages: r.reviewMessages || [],
                exceptions: r.exceptions || [],
                source: "batch",
            }))
            .filter(hasClaimCardContent);
        return claims.length ? { claims } : null;
    }

    // Bare claim_fact list/search results are not a single-claim card.
    if (Array.isArray(parsed.data?.rows) && !parsed.claimDetails) return null;

    const v4Item = parsed.claimDetails
        ?? parsed.data?.items?.[0]
        ?? null;
    const summary = parsed.claimSummary ?? null;
    const factSummary = parsed.claimFactSummary
        ?? (parsed.claimFact
            ? {
                claimHccId: parsed.claimFact.claimHccId,
                claimStatus: parsed.claimFact.claimStatus,
                claimTypeName: parsed.claimFact.claimTypeName,
                externalClaimNumber: parsed.claimFact.externalClaimNumber,
                memberKey: parsed.claimFact.memberKey,
                supplierName: parsed.claimFact.siSupplierName,
                supplierNpi: parsed.claimFact.siSupplierNpi,
            }
            : null);

    // Only render for post-submit envelopes, explicit v4 claim items, or
    // results that already carry a claimSummary / serviceLines breakdown.
    // A failed claimDetailsFetch alone (accepted / member-locked) is NOT
    // enough — that produces an empty shell card with only the claim id.
    const detailsFetched = parsed.claimDetailsFetch?.ok === true
        || !!parsed.claimDetails
        || !!parsed.claimSummary;
    const isClaimEnvelope = !!(
        detailsFetched
        || parsed.verified === true
        || (parsed.data?.items?.[0]?.header && parsed.data.items[0].claimHccId)
    );
    if (!isClaimEnvelope) return null;

    const header = v4Item?.header ?? {};
    const linesFromSummary = Array.isArray(summary?.serviceLines)
        ? summary.serviceLines
        : [];
    const linesFromV4 = extractLineDetails(v4Item).map(normalizeServiceLine);
    const serviceLines = (linesFromSummary.length
        ? linesFromSummary.map(normalizeServiceLine)
        : linesFromV4
    ).filter(Boolean);

    const totals = summary?.totalsInfo ?? header.totalsInfo ?? {};
    const statusParsed = parseClaimStatus(pick(
        factSummary?.claimStatus,
        summary?.claimStatus,
        v4Item?.claimStatus,
    ));
    const claim = {
        claimHccId: pick(
            parsed.claimHccId,
            summary?.claimHccId,
            v4Item?.claimHccId,
            factSummary?.claimHccId,
        ),
        claimStatus: statusParsed?.label ?? null,
        claimStatusCode: statusParsed?.code ?? null,
        claimStatusCss: statusParsed?.css,
        claimType: pick(
            factSummary?.claimTypeName,
            summary?.claimType,
            v4Item?.claimType,
        ),
        externalClaimNumber: pick(
            summary?.externalClaimNumber,
            header.externalClaimNumber,
            factSummary?.externalClaimNumber,
            parsed.externalClaimNumber,
        ),
        dateReceived: pick(summary?.dateReceived, header.dateReceived),
        memberName: pick(
            summary?.memberInfo?.memberName,
            header.memberInfo?.memberName,
        ),
        memberId: pick(
            summary?.memberInfo?.memberId,
            header.memberInfo?.memberId,
            factSummary?.memberKey,
        ),
        supplierName: pick(
            summary?.supplierInfo?.supplierName,
            header.supplierInfo?.supplierName,
            factSummary?.supplierName,
        ),
        totals: {
            chargedAmount: pick(totals.submittedCharges, totals.chargedAmount),
            allowedAmount: totals.allowedAmount,
            paidAmount: totals.paidAmount,
            memberAmount: pick(totals.memberAmount, totals.memberResponsibilityAmount),
        },
        serviceLines,
        claimFactSummary: factSummary,
        reviewMessages: asList(
            summary?.reviewMessages?.reviewTrigger
                ?? header.reviewMessages?.reviewTrigger,
        ).map((r) => ({
            code: r.code ?? r.reviewMessageCode?.messageCode,
            description: r.description ?? r.reviewMessageCode?.messageDescription,
        })),
        exceptions: asList(
            summary?.activityExceptions?.exception
                ?? header.activityExceptions?.exception,
            "exception",
        ).map((e) => ({
            code: e.code ?? e.exceptionMessageCode?.messageCode,
            description: e.description ?? e.exceptionMessageCode?.messageDescription,
            level: e.level ?? e.enforcementLevel,
        })),
        verified: parsed.verified,
        source: parsed.claimDetails ? "submit" : "lookup",
    };

    if (!claim.claimHccId && !claim.serviceLines.length) return null;
    // Skip id-only shells (submit ack before details, or verified:false).
    if (!hasClaimCardContent(claim)) return null;
    return { claims: [claim] };
};

const Meta = ({ label, value }) => {
    if (value == null || value === "") return null;
    return (
        <div className="agents-claim-card__meta">
            <span className="agents-claim-card__meta-label">{label}</span>
            <span className="agents-claim-card__meta-value">{value}</span>
        </div>
    );
};

const ClaimCard = memo(({ claim }) => {
    const [linesOpen, setLinesOpen] = useState(true);
    const statusClass = claim.claimStatusCss
        || String(claim.claimStatus || "").toLowerCase().replace(/\s+/g, "-");

    return (
        <div className="agents-claim-card">
            <div className="agents-claim-card__header">
                <div className="agents-claim-card__title-row">
                    <div className="agents-claim-card__title">
                        Claim {claim.claimHccId || "—"}
                    </div>
                    {claim.claimStatus && (
                        <span
                            className={`agents-claim-card__status is-${statusClass}`}
                            title={claim.claimStatusCode && claim.claimStatusCode !== claim.claimStatus
                                ? `HRP code: ${claim.claimStatusCode}`
                                : undefined}
                        >
                            {claim.claimStatus}
                        </span>
                    )}
                </div>
                <div className="agents-claim-card__subtitle">
                    {[claim.claimType, claim.externalClaimNumber
                        ? `ext ${claim.externalClaimNumber}`
                        : null]
                        .filter(Boolean)
                        .join(" · ")}
                </div>
            </div>

            <div className="agents-claim-card__grid">
                <Meta label="Member" value={
                    [claim.memberName, claim.memberId].filter(Boolean).join(" · ") || null
                } />
                <Meta label="Supplier" value={claim.supplierName} />
                <Meta label="Received" value={claim.dateReceived} />
                <Meta label="Charged" value={money(claim.totals?.chargedAmount)} />
                <Meta label="Allowed" value={money(claim.totals?.allowedAmount)} />
                <Meta label="Paid" value={money(claim.totals?.paidAmount)} />
                <Meta label="Member resp." value={money(claim.totals?.memberAmount)} />
            </div>

            {(claim.exceptions?.length > 0 || claim.reviewMessages?.length > 0) && (
                <div className="agents-claim-card__flags">
                    {claim.exceptions?.map((e, i) => (
                        <div key={`ex-${i}`} className="agents-claim-card__flag is-exception">
                            {e.code ? `${e.code}: ` : ""}{e.description || "Exception"}
                            {e.level ? ` (${e.level})` : ""}
                        </div>
                    ))}
                    {claim.reviewMessages?.map((r, i) => (
                        <div key={`rv-${i}`} className="agents-claim-card__flag is-review">
                            {r.code ? `${r.code}: ` : ""}{r.description || "Review"}
                        </div>
                    ))}
                </div>
            )}

            {claim.serviceLines?.length > 0 && (
                <>
                    <div className="agents-claim-card__lines-header">
                        <button
                            type="button"
                            className="agents-claim-card__lines-toggle"
                            onClick={() => setLinesOpen((v) => !v)}
                            aria-expanded={linesOpen}
                        >
                            Claim lines ({claim.serviceLines.length})
                            <span aria-hidden="true">{linesOpen ? "▾" : "▸"}</span>
                        </button>
                    </div>

                    {linesOpen && (
                        <div className="agents-claim-card__lines">
                            {claim.serviceLines.map((line) => (
                                <div className="agents-claim-card__line" key={line.key}>
                                    <div className="agents-claim-card__line-top">
                                        <span className="agents-claim-card__line-num">
                                            Line {line.lineNumber}
                                        </span>
                                        <span className="agents-claim-card__line-code">
                                            {line.procedureCode
                                                || (line.revenueCode
                                                    ? `Rev ${line.revenueCode}`
                                                    : "—")}
                                        </span>
                                        {line.lineStatus && (
                                            <span
                                                className={
                                                    "agents-claim-card__line-status"
                                                    + (line.lineStatusCss
                                                        ? ` is-${line.lineStatusCss}`
                                                        : "")
                                                }
                                                title={line.lineStatusCode
                                                    && line.lineStatusCode !== line.lineStatus
                                                    ? `HRP code: ${line.lineStatusCode}`
                                                    : undefined}
                                            >
                                                {line.lineStatus}
                                            </span>
                                        )}
                                        {line.benefitTierName && (
                                            <span className="agents-claim-card__line-tier">
                                                {line.benefitTierName}
                                            </span>
                                        )}
                                    </div>
                                    <div className="agents-claim-card__line-meta">
                                        {[
                                            line.serviceFromDate
                                                && `DOS ${line.serviceFromDate}${
                                                    line.serviceToDate
                                                    && line.serviceToDate !== line.serviceFromDate
                                                        ? `–${line.serviceToDate}`
                                                        : ""
                                                }`,
                                            line.units != null ? `Units ${line.units}` : null,
                                        ].filter(Boolean).join(" · ")}
                                    </div>
                                    <div className="agents-claim-card__line-amounts">
                                        {hasMoney(line.allowedAmount) && (
                                            <span>Allowed {money(line.allowedAmount)}</span>
                                        )}
                                        {hasMoney(line.paidAmount) && (
                                            <span>Paid {money(line.paidAmount)}</span>
                                        )}
                                        {hasMoney(line.memberResponsibility) && (
                                            <span>
                                                Member {money(line.memberResponsibility)}
                                            </span>
                                        )}
                                    </div>
                                    {(line.exceptions?.length > 0
                                        || line.reviewMessages?.length > 0) && (
                                        <div className="agents-claim-card__line-flags">
                                            {line.exceptions?.map((e, i) => (
                                                <div
                                                    key={`lex-${i}`}
                                                    className="agents-claim-card__flag is-exception"
                                                >
                                                    {e.code ? `${e.code}: ` : ""}
                                                    {e.description || "Exception"}
                                                </div>
                                            ))}
                                            {line.reviewMessages?.map((r, i) => (
                                                <div
                                                    key={`lrv-${i}`}
                                                    className="agents-claim-card__flag is-review"
                                                >
                                                    {r.code ? `${r.code}: ` : ""}
                                                    {r.description || "Review"}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

const ClaimResultCard = memo(({ claims }) => {
    const list = useMemo(
        () => (Array.isArray(claims) ? claims.filter(hasClaimCardContent) : []),
        [claims],
    );
    if (!list.length) return null;
    return (
        <div className="agents-msg is-claim">
            <div className="agents-msg__avatar">C</div>
            <div className="agents-msg__bubble agents-claim-card__wrap">
                {list.map((claim, i) => (
                    <ClaimCard
                        key={claim.claimHccId || `claim-${i}`}
                        claim={claim}
                    />
                ))}
            </div>
        </div>
    );
});

export default ClaimResultCard;
